import { useMemo, useState } from 'react'

import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import PortfolioTableArtwork from '~/components/atoms/PortfolioTableArtwork/PortfolioTableArtwork'
import type { ExpandableColumn } from '~/components/atoms/Table/Table'
import Table from '~/components/atoms/Table/Table'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import SearchBar from '~/components/blocks/SearchBar/SearchBar'
import { ComponentTheme } from '~/components/themes'
import useGeoLocation from '~/hooks/useLocation'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import type {
  CollectionAuctionTableData,
  CollectionSale,
} from '~/types/collectionAuctionTable'
import { currencyCodeToSymbolMap } from '~/utils/currencyMappers'
import { numberWithCommas } from '~/utils/formatCurrency'
import { toURL } from '~/utils/urlResolver'

import styles from './CollectionAuctionTable.module.css'

interface CollectionAuctionTableProps {
  data: CollectionAuctionTableData
  theme?: ComponentTheme
  smallTitle?: boolean
}

type AuctionRow = {
  artwork: JSX.Element
  auctionDate: JSX.Element
  auctionHouse: JSX.Element
  returnToSeller: JSX.Element
  hammerPrice: JSX.Element
  buyerPaid: JSX.Element
}

const CollectionAuctionTable = ({
  data,
  theme = ComponentTheme.primary,
  smallTitle = false,
}: CollectionAuctionTableProps) => {
  const tAuction = useTranslations('auctionHistoryTable')
  const tCommon = useTranslations('common')
  const { activeCurrencyIso } = useGeoLocation()
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const router = useRouter()
  const [showAll, setShowAll] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const tableLimit = isMobile ? 3 : 8

  const buildArtworkUrl = (sale: (typeof data.sales)[0]) => {
    if (!sale.artistSlug || !sale.artworkSlug) return null

    return toURL({
      slug: sale.artworkSlug,
      artist: sale.artistSlug,
      collection: sale.collectionSlug,
      collectionTaxonomy: sale.collectionTaxonomy,
      _type: 'artwork',
    })
  }

  const handleRowClick = (sale: (typeof data.sales)[0]) => {
    const url = buildArtworkUrl(sale)
    if (url) {
      router.push(url)
    }
  }

  const filteredSales = useMemo(() => {
    if (!data?.sales) return []
    if (!searchQuery.trim()) return data.sales

    return data.sales.filter(sale =>
      sale.artwork_title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [data?.sales, searchQuery])

  const columns: ExpandableColumn<AuctionRow>[] = useMemo(
    () => [
      {
        Header: (
          <Text uppercase size={TextSizes.bodySml}>
            {tAuction('artwork')}
          </Text>
        ),
        minWidth: 200,
        accessor: 'artwork',
      },
      {
        Header: (
          <Text uppercase size={TextSizes.bodySml}>
            {tAuction('auctionDate')}
          </Text>
        ),
        maxWidth: 120,
        accessor: 'auctionDate',
      },
      {
        Header: (
          <Text uppercase size={TextSizes.bodySml}>
            {tAuction('auctionHouse')}
          </Text>
        ),
        maxWidth: 150,
        accessor: 'auctionHouse',
      },
      {
        Header: (
          <Text uppercase size={TextSizes.bodySml}>
            {tAuction('returnToSeller')}
          </Text>
        ),
        maxWidth: 120,
        accessor: 'returnToSeller',
      },
      {
        Header: (
          <Text uppercase size={TextSizes.bodySml}>
            {tAuction('hammerPrice')}
          </Text>
        ),
        maxWidth: 120,
        accessor: 'hammerPrice',
      },
      {
        Header: (
          <Text uppercase size={TextSizes.bodySml}>
            {tAuction('buyerPaid')}
          </Text>
        ),
        maxWidth: 120,
        accessor: 'buyerPaid',
      },
    ],
    [tAuction]
  )

  const displayedSales = useMemo(() => {
    if (!filteredSales.length) return []
    return showAll ? filteredSales : filteredSales.slice(0, tableLimit)
  }, [filteredSales, showAll, tableLimit])

  const currencyIso = activeCurrencyIso || 'GBP'

  const getSaleValuesByCurrency = (
    salesArr: CollectionSale,
    currencyIso: any
  ) => {
    return (
      salesArr.currencies?.find(curr => curr.iso === currencyIso) ||
      salesArr.currencies?.find(curr => curr.iso === 'GBP') ||
      salesArr.currencies?.[0]
    )
  }

  const tableData = useMemo(() => {
    if (!displayedSales.length) return []

    return displayedSales.map(sale => {
      const formattedDate = sale.date_of_sale
        ? new Date(sale.date_of_sale).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : 'N/A'

      const saleCurrencyData = getSaleValuesByCurrency(sale, currencyIso)

      return {
        artwork: (
          <PortfolioTableArtwork
            item={{
              artwork: {
                image: sale.image,
                title: sale.artwork_title,
                format: sale.format,
                signed: sale.signed,
                artist: sale.artist,
              },
              listPriceMin: sale.listPriceMin,
              listPriceMax: sale.listPriceMax,
              listPriceMinValues: sale.listPriceMinValues,
              listPriceMaxValues: sale.listPriceMaxValues,
              poa: false,
              artistSlug: sale.artistSlug,
              artworkSlug: sale.artworkSlug,
              collectionSlug: sale.collectionSlug,
              collectionTaxonomy: sale.collectionTaxonomy,
            }}
            linkArtwork={true}
            extendedInfo={true}
          />
        ),
        auctionDate: (
          <div className={styles.cell} onClick={() => handleRowClick(sale)}>
            <Text size={TextSizes.bodySml}>{formattedDate}</Text>
          </div>
        ),
        auctionHouse: (
          <div className={styles.cell} onClick={() => handleRowClick(sale)}>
            <Text size={TextSizes.bodySml}>{sale.auction_house_name}</Text>
          </div>
        ),
        returnToSeller: (
          <div className={styles.cell} onClick={() => handleRowClick(sale)}>
            <Text size={TextSizes.bodySml}>
              {currencyCodeToSymbolMap[currencyIso]}
              {numberWithCommas(saleCurrencyData?.seller_net_return ?? 0)}
            </Text>
          </div>
        ),
        hammerPrice: (
          <div className={styles.cell} onClick={() => handleRowClick(sale)}>
            <Text size={TextSizes.bodySml}>
              {currencyCodeToSymbolMap[currencyIso]}
              {numberWithCommas(saleCurrencyData?.hammer ?? 0)}
            </Text>
          </div>
        ),
        buyerPaid: (
          <div className={styles.cell} onClick={() => handleRowClick(sale)}>
            <Text size={TextSizes.bodySml}>
              {currencyCodeToSymbolMap[currencyIso]}
              {numberWithCommas(saleCurrencyData?.paid ?? 0)}
            </Text>
          </div>
        ),
      }
    })
  }, [displayedSales, activeCurrencyIso])

  return (
    <Fixture verticalPadding={true} theme={theme}>
      <Container gutters={gutterTypes.always}>
        <Text
          Tag="h3"
          size={TextSizes.primaryTitleLrg}
          className={styles.title}
        >
          {tAuction('auctionResults')}
        </Text>

        <div className={styles.searchContainer}>
          <SearchBar
            placeHolderText={tAuction('searchArtwork')}
            onChange={value => setSearchQuery(value)}
          />
        </div>
      </Container>

      <Container>
        {isMobile ? (
          <div className={styles.mobileTableContainer}>
            <div className={styles.mobileTable}>
              {displayedSales.map((sale, i) => {
                const saleCurrencyData =
                  sale.currencies?.find(curr => curr.iso === currencyIso) ||
                  sale.currencies?.find(curr => curr.iso === 'GBP') ||
                  sale.currencies?.[0]
                const formattedDate = sale.date_of_sale
                  ? new Date(sale.date_of_sale).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : 'N/A'

                return (
                  <div
                    key={`mobile-row-${i}`}
                    className={styles.mobileRow}
                    onClick={() => handleRowClick(sale)}
                  >
                    <div className={styles.mobileRowArtwork}>
                      <Text
                        Tag="p"
                        size={TextSizes.secondaryTitleMed}
                        className={styles.artworkTitle}
                      >
                        {sale.artwork_title}
                      </Text>
                      {sale.format && (
                        <Text
                          Tag="p"
                          size={TextSizes.bodyXSml}
                          className={styles.formatText}
                          uppercase
                        >
                          {sale.signed && sale.signed} {sale.format}
                        </Text>
                      )}
                      {sale.artist && (
                        <Text
                          Tag="p"
                          size={TextSizes.primaryTitleSml}
                          className={styles.artistText}
                        >
                          {sale.artist}
                        </Text>
                      )}
                      {sale.listPriceMin && sale.listPriceMax && (
                        <Text
                          Tag="p"
                          size={TextSizes.bodyXSml}
                          className={styles.priceText}
                        >
                          {currencyCodeToSymbolMap[activeCurrencyIso || 'GBP']}
                          {numberWithCommas(sale.listPriceMin)} -{' '}
                          {currencyCodeToSymbolMap[activeCurrencyIso || 'GBP']}
                          {numberWithCommas(sale.listPriceMax)}
                        </Text>
                      )}
                    </div>

                    <div className={styles.mobileRowHeader}>
                      <Text size={TextSizes.bodySml}>
                        {sale.auction_house_name}
                      </Text>
                      <Text size={TextSizes.bodySml}>{formattedDate}</Text>
                    </div>

                    <div className={styles.mobileRowPrices}>
                      <div>
                        <Text
                          size={TextSizes.bodySml}
                          weight="bold"
                          Tag="p"
                          uppercase
                        >
                          {tAuction('hammerPrice')}
                        </Text>
                        <Text size={TextSizes.bodySml}>
                          {currencyCodeToSymbolMap[activeCurrencyIso || 'GBP']}
                          {numberWithCommas(saleCurrencyData?.hammer ?? 0)}
                        </Text>
                      </div>

                      <div>
                        <Text
                          size={TextSizes.bodySml}
                          weight="bold"
                          Tag="p"
                          uppercase
                        >
                          {tAuction('returnToSeller')}
                        </Text>
                        <Text size={TextSizes.bodySml}>
                          {currencyCodeToSymbolMap[activeCurrencyIso || 'GBP']}
                          {numberWithCommas(
                            saleCurrencyData?.seller_net_return ?? 0
                          )}
                        </Text>
                      </div>

                      <div>
                        <Text
                          size={TextSizes.bodySml}
                          weight="bold"
                          Tag="p"
                          uppercase
                        >
                          {tAuction('buyerPaid')}
                        </Text>
                        <Text size={TextSizes.bodySml}>
                          {currencyCodeToSymbolMap[activeCurrencyIso || 'GBP']}
                          {numberWithCommas(saleCurrencyData?.paid ?? 0)}
                        </Text>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div
            className={classNames(styles.tableWrapper, {
              [styles.smallTitle]: smallTitle,
            })}
          >
            <Table<AuctionRow>
              name="collection-auction-table"
              columns={columns}
              data={tableData as any}
              minHeight={200}
              noMarginTop
            />
          </div>
        )}
      </Container>

      {filteredSales.length > tableLimit && (
        <Container gutters={gutterTypes.always}>
          <div className={styles.buttonContainer}>
            <Button
              variant={ButtonVariants.secondaryDark}
              iconLeft={true}
              button={{
                type: ButtonTypes.button,
                onClick: () => setShowAll(!showAll),
              }}
            >
              {showAll ? tCommon('showLess') : tCommon('loadMore')}
            </Button>
          </div>
        </Container>
      )}
    </Fixture>
  )
}

export default CollectionAuctionTable
